// ABOUTME: Keys of the node assignment columns (owner, dealership, client, market) of the DataTableWrapper.
// ABOUTME: Kept apart from the wrapper so consumers can whitelist them without importing the grid.

import type { AssignedRefField } from './assignedRefs';

// Node assignment columns. Owner, dealership and client (`system-assigned<Field>`, filterable)
// are rendered when the object schema enables node ownership; market (`system-assigned-market`)
// when the market module is on for the tenant. Consumers narrow them through the wrapper's
// `assignmentColumns` prop.
export const ASSIGNMENT_COLUMN_KEYS = ['owner', 'dealership', 'client', 'market'] as const;
export type AssignmentColumnKey = (typeof ASSIGNMENT_COLUMN_KEYS)[number];

// Node field each assignment column shows. The field name is also accepted in a
// `displayContentTypes` whitelist and, prefixed with `system-`, is the column id the search
// filter of the owner/dealership/client columns is sent on.
export const ASSIGNMENT_COLUMN_FIELDS = {
    owner: 'assignedOwner',
    dealership: 'assignedDealership',
    client: 'assignedClient',
    market: 'assignedMarket',
} as const satisfies Record<AssignmentColumnKey, AssignedRefField>;

// Column id of each assignment column in the grid, e.g. for a consumer's `initialColsOrder`.
export const ASSIGNMENT_COLUMN_IDS = {
    owner: `system-${ASSIGNMENT_COLUMN_FIELDS.owner}`,
    dealership: `system-${ASSIGNMENT_COLUMN_FIELDS.dealership}`,
    client: `system-${ASSIGNMENT_COLUMN_FIELDS.client}`,
    market: 'system-assigned-market',
} as const satisfies Record<AssignmentColumnKey, string>;

/**
 * The assignment columns the table shows, in table order: the ownership ones need the schema's
 * `enableOwner`, the market one needs the market module, and `assignmentColumns` (when given)
 * narrows both.
 */
export function getVisibleAssignmentColumnKeys({
    ownerEnabled,
    marketEnabled,
    assignmentColumns,
}: {
    ownerEnabled: boolean;
    marketEnabled: boolean;
    assignmentColumns?: AssignmentColumnKey[];
}): AssignmentColumnKey[] {
    return ASSIGNMENT_COLUMN_KEYS.filter(key =>
        key === 'market' ? marketEnabled : ownerEnabled,
    ).filter(key => !assignmentColumns || assignmentColumns.includes(key));
}
